import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import ChatbotPanel from './ChatbotPanel';
import config from '../../config';

/**
 * 우측 하단 플로팅 버튼 + 챗봇 패널 진입점
 *
 * - 챗봇 대화 플로우는 `ChatbotPanel` (공통 컴포넌트) 으로 분리
 * - 채널톡 SDK 로드는 환경변수(CHANNEL_TALK_KEY) 존재 시에만 작동
 */
export default function FloatingButtons() {
  const { t } = useTranslation();
  const [showScroll, setShowScroll] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  // #264: 하단 sticky CTA 바의 '실제 높이'를 측정해 그 위로 띄운다(고정 offset 으론 바가 높으면 겹침).
  const [stickyBarHeight, setStickyBarHeight] = useState(0);

  const hasChannelTalk = !!config.CHANNEL_TALK_KEY;

  // 채널톡 SDK 로드 (키 설정 시에만)
  useEffect(() => {
    if (!hasChannelTalk) return;
    if (window.ChannelIO) return;
    const ch = function () { ch.c(arguments); };
    ch.q = [];
    ch.c = function (args) { ch.q.push(args); };
    window.ChannelIO = ch;
    const script = document.createElement('script');
    script.src = 'https://cdn.channel.io/plugin/ch-plugin-web.js';
    script.async = true;
    script.onload = () => { window.ChannelIO('boot', { pluginKey: config.CHANNEL_TALK_KEY }); };
    document.head.appendChild(script);
    return () => { if (window.ChannelIO) window.ChannelIO('shutdown'); };
  }, [hasChannelTalk]);

  // 스크롤 탑 버튼 노출 여부
  useEffect(() => {
    const handleScroll = () => setShowScroll(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // #264: 하단 sticky CTA 바(data-sticky-bottom-bar)의 실제 높이를 측정 → 플로팅을 (바높이 + 여백) 만큼 띄움.
  //   바가 있으면 그 위로, 없으면 화면 하단(16px). 바 등장/제거·높이변화·리사이즈 모두 추적.
  useEffect(() => {
    const measure = () => {
      const bar = document.querySelector('[data-sticky-bottom-bar]');
      setStickyBarHeight(bar ? Math.round(bar.getBoundingClientRect().height) : 0);
    };
    measure();
    const observer = new MutationObserver(measure);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', measure);
    return () => { observer.disconnect(); window.removeEventListener('resize', measure); };
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <>
      {/* 챗봇 패널 (대화 플로우는 ChatbotPanel 에서 처리) */}
      {chatOpen && <ChatbotPanel onClose={() => setChatOpen(false)} />}

      {/* 플로팅 버튼 — 하단 sticky CTA 바가 있으면 '바 높이 + 16px' 만큼 위로, 없으면 16px(가장 하단).
          바 높이를 실측하므로 바가 더 높아져도 항상 위로 떠 겹치지 않음(#264). */}
      <div
        className="fixed right-4 z-50 flex flex-col items-center gap-3"
        style={{ bottom: `${(stickyBarHeight ? stickyBarHeight + 16 : 16)}px` }}
      >
        <button
          onClick={() => setChatOpen(!chatOpen)}
          className={`w-14 h-14 rounded-full shadow-xl ring-2 ring-white flex items-center justify-center transition-all hover:scale-110 active:scale-95 ${
            chatOpen ? 'bg-orange-600 rotate-0' : 'bg-orange-500'
          }`}
          style={{ boxShadow: '0 8px 20px -4px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.5)' }}
          aria-label={t('common.customer_center_label')}
          title={t('common.customer_center_label')}
        >
          {chatOpen ? (
            <Icon name="close" size={24} className="text-white" />
          ) : (
            <Icon name="headset" size={26} className="text-white" />
          )}
        </button>

        {showScroll && !chatOpen && (
          <button
            onClick={scrollToTop}
            className="w-10 h-10 rounded-full bg-white shadow-md border border-mono-200 flex items-center justify-center text-mono-500 hover:text-mono-700 transition-colors"
            aria-label={t('common.scroll_top')}
          >
            <Icon name="chevron-up" size={18} />
          </button>
        )}
      </div>
    </>
  );
}
